import React, { useEffect, useRef, useState } from "react";
import "./MyOrders.css";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Button } from "primereact/button";
import { Image } from "primereact/image";
import useOrderAPI from "../../../../../api/useOrderAPI";
import miniDot from "../../../../../assets/images/mini_dot.png";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { Paginator } from "primereact/paginator";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../../../../redux/loadingSlice";
import noProductFoundImage from "../../../../../assets/images/product-not-found.png";
import AppSkeleton from "../../../../AppSkeleton/AppSkeleton";

interface Item {
  id: number;
  name: string;
  rim: string;
  price: string;
  quantity: number;
  total: string;
}

const MyOrders = () => {
  const { allOrders, allOrdersCount, changeOrderStatus } = useOrderAPI();
  const loading = useSelector((state: any) => state.loading);
  const dispatch = useDispatch();

  const toast = useRef<Toast>(null);

  const [items, setItems] = useState<any[]>([]);
  const [userId, setUserId] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [isShowPrice, setIsShowPrice] = useState(false) as any;
  const [searchTerm, setSearchTerm] = useState("");
  const [debounceTimeoutId, setDebounceTimeoutId] =
    useState<NodeJS.Timeout | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [rowsPerPageOption, setRowsPerPageOption] = useState(10);
  const [records, setRecords] = useState(20);
  const [loader, setLoader] = useState(false);

  const fetchMyOrders = async (searchTerm?: string) => {
    try {
      setLoader(true);
      const payload = {
        company_id: companyId,
        search_by: searchTerm,
        user_id: userId,
        page_number: pageNumber,
        page_size: rowsPerPageOption,
      };

      const response: any = await allOrders(payload);
      const responseCount: any = await allOrdersCount(payload);
      if (response.status === 200 || response.status === 201) {
        dispatch(setLoading(false));
        setItems(response?.data);
        setRecords(responseCount?.data?.data);
      } else {
        dispatch(setLoading(false));
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoader(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    if (debounceTimeoutId) clearTimeout(debounceTimeoutId);
    setDebounceTimeoutId(setTimeout(() => fetchMyOrders(newSearchTerm), 300));
  };

  useEffect(() => {
    dispatch(setLoading(true));
    const authTokens: any = JSON.parse(
      localStorage.getItem("authTokens") || "{}",
    );

    setUserId(authTokens?.user?.id);
    setCompanyId(authTokens?.user?.company_id);
    if (userId !== "") {
      fetchMyOrders();
    }

    if (authTokens?.user?.is_show_price === true) {
      setIsShowPrice(true);
    }
  }, [userId, pageNumber, rowsPerPageOption]);

  const handleAcceptReject = async (order_id: number, type: string) => {
    const payload = {
      order_id: order_id,
      status_code: type,
    };

    const response: any = await changeOrderStatus(payload);
    if (response.status === 200 || response.status === 201) {
      fetchMyOrders();
      return toast?.current?.show({
        severity: "success",
        summary: "Success",
        detail: `${response?.data?.message}`,
      });
    } else {
      return toast?.current?.show({
        severity: "error",
        summary: "Failed",
        detail: `Failed to change status`,
      });
    }
  };

  const downloadFile = (url: string, fileName: string) => {
    const a = document.createElement("a");
    a.target = "_blank";
    a.href = url;
    a.download = fileName || "download";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const onPageChange = (e: any) => {
    const newPageNumber = e.first / e.rows + 1;
    setPageNumber(newPageNumber);
    setRowsPerPageOption(e.rows);
  };

  const paginatorTemplate = {
    layout:
      "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown",

    CurrentPageReport: (options: any) => {
      return (
        <span
          style={{
            color: "#6c757d",
            userSelect: "none",
            width: "120px",
            textAlign: "center",
            display: "block",
          }}
        >
          {options.first} to {options.last} of {options.totalRecords}
        </span>
      );
    },
  };

  return (
    <>
      <Toast ref={toast} />

      <div className="my-order-wrapper">
        <div className="my-order-header-wrapper">
          <div className="my-order-title">My Orders</div>

          <div className="my-order-search-input">
            {/* <span className="p-input-icon-left">
              <i className="pi pi-search" />
              <InputText
                placeholder="Search"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </span> */}
          </div>
        </div>

        {loader && (
          <AppSkeleton
            count={6}
            width="550px"
            height="100px"
            borderRadius="12px"
            containerClassName="popular-brands-skeleton-grid"
            className="popular-brands-skeleton-item"
          />
        )}

        {!loader && items.length === 0 && (
          <div className="no-products">
            <Image
              src={noProductFoundImage}
              alt="No Product Found"
              className="no-products-image"
            />
            <div className="no-products-text">No Products Available</div>
          </div>
        )}

        {items.map((item: any) => (
          <div
            key={`order_${item.order_id}`}
            className="my-order-single-order-container"
          >
            <div className="my-order-single-order">
              {/* ---------- TOP SUMMARY BAR (Order details first) ---------- */}
              <div className="order-summary-bar">
                <div className="order-summary-left">
                  <div className="order-summary-id">
                    Order #{item?.order_id || "Not Available"}
                  </div>
                  <div className="order-summary-date">
                    Placed on{" "}
                    {new Date(item?.created_at).toLocaleDateString("en-IN", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    }) || "Not Available"}
                  </div>
                </div>

                <div className="order-summary-right">
                  {isShowPrice && (
                    <div className="order-summary-total">
                      <span className="order-summary-total-label">Total</span>
                      <span className="order-summary-total-value">
                        &#8377;{item?.payable_amount || "0"}
                      </span>
                    </div>
                  )}

                  <div className="item-header-actions">
                    {item?.invoice_url && (
                      <Button
                        label="Download Invoice"
                        icon="pi pi-download"
                        className="p-button-text p-button-plain item-header-download-invoice"
                        onClick={() =>
                          downloadFile(
                            item?.invoice_url,
                            `order-id-${item.order_id}`,
                          )
                        }
                      />
                    )}

                    <div
                      className={`item-header-status-indicator ${item?.status?.name
                        ?.toLowerCase()
                        .replace(/\s/g, "-")}`}
                    >
                      {item?.status?.name}
                    </div>
                  </div>
                </div>
              </div>

              <div className="items-container">
                {/* ---------- PRODUCT ITEMS ---------- */}
                <ul className="item-list">
                  {item?.order_items?.map((itm: any) => {
                    const productName =
                      itm?.product?.product_name ??
                      itm?.service?.service_name ??
                      "Not Available";

                    return (
                      <li key={`order_${itm?.order_item_id}`} className="item1">
                        <div className="item-image-wrapper">
                          {/* <div className="item-name-overlay">{productName}</div> */}
                          <Image
                            src={
                              itm?.product?.document_as_product?.[0]?.document
                                ?.file_path ||
                              "https://mechmiles.com/not-available.png"
                            }
                            alt={productName}
                            className="item-image"
                          />
                        </div>

                        <div className="item-body">
                          <div className="item-name">
                            {itm?.product?.brand?.name ||
                              itm?.service?.service_name}
                          </div>
                          <div className="item-meta-row">
                            {isShowPrice && (
                              <div className="item-price">
                                Price: &#8377;
                                {itm?.product?.product_offer_price ||
                                  Math.round(
                                    itm?.total_amount / itm?.item_quantity,
                                  )}
                              </div>
                            )}
                            <div className="item-quantity">
                              Qty: {itm?.item_quantity || 0}
                            </div>
                            {isShowPrice && (
                              <div className="item-total">
                                &#8377;{itm?.total_amount || 0}
                              </div>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                {isShowPrice && (
                  <div className="order-billing-summary">
                    <div className="order-billing-header">
                      <div>
                        <div className="order-billing-title">Order Summary</div>
                        <div className="order-billing-subtitle">
                          Payment details
                        </div>
                      </div>
                    </div>

                    <div className="order-billing-body">
                      {/* Subtotal */}
                      {item?.amount !== undefined && item?.amount !== null && (
                        <div className="order-billing-row">
                          <span className="order-billing-label">Subtotal</span>

                          <span className="order-billing-value">
                            ₹{Number(item?.amount).toFixed(2)}
                          </span>
                        </div>
                      )}

                      {/* GST */}
                      {item?.tax !== undefined && item?.tax !== null && (
                        <div className="order-billing-row">
                          <span className="order-billing-label">GST</span>

                          <span className="order-billing-value">
                            ₹{Number(item?.tax).toFixed(2)}
                          </span>
                        </div>
                      )}

                      {/* Fitment / Platform / Other Charges */}
                      {item?.additional_charges?.map((charge: any) => (
                        <div
                          className="order-billing-row"
                          key={`charge_${charge?.id}`}
                        >
                          <span className="order-billing-label">
                            {charge?.name}
                          </span>

                          <span className="order-billing-value">
                            ₹{Number(charge?.amount || 0).toFixed(2)}
                          </span>
                        </div>
                      ))}

                      {/* Coupon Discount */}
                      {item?.coupon_discount !== undefined &&
                        item?.coupon_discount !== null &&
                        Number(item?.coupon_discount) > 0 && (
                          <div className="order-billing-row order-billing-discount">
                            <span className="order-billing-label">
                              Coupon Discount
                            </span>

                            <span className="order-billing-value">
                              -₹{Number(item?.coupon_discount).toFixed(2)}
                            </span>
                          </div>
                        )}

                      {/* Divider */}
                      {item?.payable_amount !== undefined &&
                        item?.payable_amount !== null && (
                          <div className="order-billing-divider" />
                        )}

                      {/* Final Total */}
                      {item?.payable_amount !== undefined &&
                        item?.payable_amount !== null && (
                          <div className="order-billing-total-row">
                            <div>
                              <div className="order-billing-total-label">
                                Total Amount
                              </div>
                              <div className="order-billing-total-subtitle">
                                Amount paid
                              </div>
                            </div>

                            <div className="order-billing-total-value">
                              ₹{Number(item?.payable_amount).toFixed(2)}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                )}
                {/* ---------- SECONDARY DETAILS (service point, billing, vehicle) ---------- */}
                <div className="order-details-grid">
                  <div className="order-details-card">
                    <div className="order-id-title">Service Point Address</div>
                    <div className="order-id-content">
                      {item?.service_point?.service_point_address ||
                        "Not Available"}
                    </div>
                    <div className="order-id-content">
                      Store Name:{" "}
                      {item?.service_point?.service_point_name ||
                        "Not Available"}
                    </div>
                  </div>

                  <div className="order-details-card">
                    <div className="order-id-title">Billing Address</div>
                    <div className="order-id-content">
                      {item?.billing_address?.city?.name || "Not Available"},{" "}
                      {item?.billing_address?.city?.field3 || "Not Available"} (
                      {item?.billing_address?.pincode || "Not Available"})
                    </div>
                  </div>

                  <div className="order-details-card">
                    <div className="order-id-title">Payment Status</div>
                    <div className="order-id-content">
                      {item?.payment_status?.name || "***"}
                    </div>
                  </div>

                  <div className="order-details-card">
                    <div className="order-id-title">Vehicle Details</div>
                    <div className="order-id-content">
                      {item?.vehicle?.category?.name || "Not Available"} ·{" "}
                      {item?.vehicle?.make?.name || "Not Available"} ·{" "}
                      {item?.vehicle?.model?.name || "Not Available"}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* <div className="my-order-action-buttons">
              <Button
                className="cancel-item-button"
                onClick={() => {
                  handleAcceptReject(item?.order_id, "REJECTED");
                }}
              >
                Cancel Item
              </Button>
              <Button className="view-appointment-button">
                View Appointment
              </Button>
            </div> */}
          </div>
        ))}

        {items.length > 0 && (
          <Paginator
            first={(pageNumber - 1) * rowsPerPageOption}
            rows={rowsPerPageOption}
            totalRecords={records}
            rowsPerPageOptions={[5, 10, 25, records]}
            onPageChange={onPageChange}
            template={paginatorTemplate}
          />
        )}
      </div>
    </>
  );
};

export default MyOrders;
